import { z, toJSONSchema } from 'zod';
import Cookies from 'js-cookie';
import { TENANT_STORAGE_KEY } from '@constants/tenant';
import { CartService } from '@services/cart';
import { useServiceRef } from 'hooks/useServiceRef';
import { getCartDraft } from '@utils/getCartDraft';
import { McpService } from '../mcp';
import { InputSchema, ToolDescriptor, ToolResponse } from '@mcp-b/webmcp-types';

const schema = z.object({
  productId: z.string().describe('The ID of the product to add to the cart.'),
  variantId: z.number().describe('The ID of the variant to add to the cart.'),
  quantity: z.number().describe('The quantity of the product to add to the cart.'),
  currency: z.string().describe('The currency of the product to add to the cart, for example: "USD"').optional()
});

export const addToCartTool: ToolDescriptor<z.infer<typeof schema>, ToolResponse> = {
  name: 'addToCart',
  description: `Add a line item to the current user's cart. Schema: ${JSON.stringify(schema.shape)}`,
  inputSchema: toJSONSchema(schema) as InputSchema,
  execute: async ({
    productId,
    variantId,
    quantity = 1,
    currency = 'USD'
  }: z.infer<typeof schema>): Promise<ToolResponse> => {
    const cartServiceRef = useServiceRef(CartService);
    const mcpServiceRef = useServiceRef(McpService);

    const tenantKey = JSON.parse(Cookies.get(TENANT_STORAGE_KEY)|| null)?.key;
    let cart = await cartServiceRef.current.getMyActiveCart(tenantKey);

    const lineItemDraft = {
      productId,
      variantId,
      quantity
    };

    if (!cart) {
      const cartDraft = getCartDraft(lineItemDraft, currency);
      cart = await cartServiceRef.current.createCart(cartDraft);
    } else {
      cart = await cartServiceRef.current.addLineItem(
        cart?.id,
        cart?.version,
        lineItemDraft
      );
    }

    if (cart) {
      mcpServiceRef.current.triggerCartUpdate(cart);
    }

    return {
      content: [ {
        type: 'text',
        text: `Updated cart with ${cart?.lineItems?.length || 0} items and total price of ${cart?.totalPrice?.value || 0}. Cart: ${JSON.stringify(cart)}.`,
      } ]
    };
  }
};
